<template>
  <header class="sp-finder-result-heading" part="base">
    <sp-text tag="h2" type="h3" class="sp-finder-result-heading__title" part="title">
      <a
        v-if="url"
        class="sp-finder-result-heading__title-link"
        :href="url"
        :data-testid="TestId.finder.resultLink"
        translate="no"
      >
        {{ name }}
      </a>
      <span v-else translate="no">{{ name }}</span>
    </sp-text>

    <sp-text
      v-if="hasSpecialtyText"
      tag="p"
      type="body"
      class="sp-finder-result-heading__specialties"
      part="specialties"
    >
      <span v-if="showProviderType">{{ providerType }}</span>
      <template v-if="showProviderType">, </template>
      <template v-for="(specialty, index) in specialties" :key="`${specialty.name}-${index}`">
        <template v-if="index > 0">, </template>
        <a
          v-if="specialty.href"
          class="sp-finder-result-heading__specialty-link"
          :href="specialty.href"
          @click="handleSpecialtyClick($event, specialty)"
        >
          {{ specialty.name }}
        </a>
        <span v-else>{{ specialty.name }}</span>
      </template>
    </sp-text>
  </header>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";

const emit = defineEmits(["add-filters"]);

const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  url: {
    type: String,
    default: undefined,
  },
  providerType: {
    type: String,
    default: "",
  },
  skills: {
    type: Array,
    default: () => [],
  },
  showSpecialty: {
    type: Boolean,
    default: false,
  },
});

const specialties = computed(() => props.skills.filter((skill) => skill?.name).map(mapSkill));
const hasSpecialties = computed(() => specialties.value.length > 0);
const showProviderType = computed(() => Boolean(props.providerType && hasSpecialties.value));
const hasSpecialtyText = computed(() => props.showSpecialty && hasSpecialties.value);

function mapSkill(skill) {
  const hasFilter = Boolean(skill.filter && skill.parameterize);
  const filters = hasFilter ? { [skill.filter]: skill.parameterize } : undefined;
  const href = hasFilter && !skill.selected ? `?${new URLSearchParams(filters).toString()}` : undefined;

  return { name: skill.name, href, filters };
}

function handleSpecialtyClick(event, specialty) {
  if (shouldUseNativeLinkBehavior(event) || !specialty.filters) {
    return;
  }

  event.preventDefault();
  emit("add-filters", specialty.filters);
}

function shouldUseNativeLinkBehavior(event) {
  return (
    event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  );
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-heading {
  /* Layout */
  --title-margin: var(--sp-comp-finder-result-heading-title-margin, 0);
  --specialties-margin: var(--sp-comp-finder-result-heading-specialties-margin, var(--sp-ref-spacing-2) 0 0);

  /* Title */
  --title-color: var(--sp-comp-finder-result-heading-title-color, var(--sp-sys-color-primary, #691f74));

  /* Specialties */
  --specialties-color: var(--sp-comp-finder-result-heading-specialties-color, var(--sp-sys-color-on-surface));

  min-width: 0;

  &__title {
    --sp-comp-text-color: var(--title-color);
    margin: var(--title-margin);
  }

  &__title-link,
  &__specialty-link {
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }
  }

  &__specialties {
    --sp-comp-text-color: var(--specialties-color);
    margin: var(--specialties-margin);
  }
}
</style>
